import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { COUNTRIES, POPULAR_COUNTRIES, flagEmoji } from "@/data/countries";
import { useCountry } from "@/contexts/CountryContext";
import { Check, Globe, X } from "lucide-react";

export default function CountryPicker({ compact = false }) {
  const { code, country, setCode, clear } = useCountry();
  const [open, setOpen] = useState(false);

  const label = country
    ? (compact ? `${flagEmoji(code)} ${code}` : `${flagEmoji(code)} ${country.name}`)
    : (compact ? "🌍" : "🌍 Global");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="country-picker-trigger"
          aria-label="Select country"
          className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:bg-[color:var(--wofy-surface)] transition-colors"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <span className="text-base leading-none">{country ? flagEmoji(code) : <Globe className="w-4 h-4 inline" />}</span>
          <span className={compact ? "font-mono text-xs" : ""}>{country ? (compact ? code : country.name) : "Global"}</span>
          {country && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); clear(); setOpen(false); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); clear(); setOpen(false); } }}
              className="ml-1 opacity-60 hover:opacity-100"
              aria-label="Clear country"
              data-testid="country-picker-clear"
            >
              <X className="w-3 h-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Search country…" data-testid="country-picker-search" />
          <CommandList className="max-h-[400px]">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandItem
              value="__global__"
              onSelect={() => { clear(); setOpen(false); }}
              data-testid="country-option-global"
              className="cursor-pointer"
            >
              <Globe className="w-4 h-4 mr-2" />
              <span>All countries (Global)</span>
              {!code && <Check className="ml-auto w-4 h-4" />}
            </CommandItem>
            <CommandSeparator />
            <CommandGroup heading="⭐ Popular">
              {POPULAR_COUNTRIES.map((c) => (
                <CommandItem
                  key={`pop-${c.code}`}
                  value={`popular ${c.name} ${c.code}`}
                  onSelect={() => { setCode(c.code); setOpen(false); }}
                  data-testid={`country-option-pop-${c.code}`}
                  className="cursor-pointer"
                >
                  <span className="text-base mr-2">{flagEmoji(c.code)}</span>
                  <span>{c.name}</span>
                  <span className="ml-2 font-mono text-[10px] opacity-50">{c.code}</span>
                  {code === c.code && <Check className="ml-auto w-4 h-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="All countries">
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.name} ${c.code}`}
                  onSelect={() => { setCode(c.code); setOpen(false); }}
                  data-testid={`country-option-${c.code}`}
                  className="cursor-pointer"
                >
                  <span className="text-base mr-2">{flagEmoji(c.code)}</span>
                  <span>{c.name}</span>
                  <span className="ml-2 font-mono text-[10px] opacity-50">{c.code}</span>
                  {code === c.code && <Check className="ml-auto w-4 h-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
